// "When did this reader last open that screen, on this device?" — the persistence half of the summary
// tiles' "new" badge. The counting half is `lib/summary-freshness.ts`; this module only remembers the
// mark. The React side is `lib/use-last-visited.ts`.
//
// Kept import-free on purpose so the unit test exercises it without dragging the app's dependency
// graph (see orion-web/CLAUDE.md → "Tests (jest)"), and storage is injectable for the same reason —
// these tests run in `node`, where there is no window at all.
//
// Two things this module refuses to do, both of which are the difference between a badge and a bug:
//
//   1. **It never throws.** There is not always a localStorage: SSR has no `window`, a Safari
//      private-mode window throws on the mere ACCESS rather than on the read, a full quota throws on
//      the write, and an embedded webview can have storage off entirely. A summary badge is decoration
//      on a screen full of real content, so a storage failure degrades to "no badge" — it must never
//      take the screen down.
//   2. **It never trusts what it reads back.** The store is user-writable, so a hand-edited or stale
//      value must not reach the arithmetic. Anything non-numeric reads as "no visit recorded", which
//      `newSince` already has a defined answer for (the last 24 hours).

/**
 * The summary surfaces a reader can open, and which the hub tiles badge against.
 *
 * `home` is the odd one: it badges no tile. The Overview home stamps itself on arrival and reads its
 * own stamp back for the "N new updates since your last visit" line above its category cards — the
 * same question the tiles ask, about the screen the reader is standing on rather than one they might
 * go to. It shares this module because the two decisions that make the badge honest (a first visit is
 * the last 24 hours, not "everything"; a clock in the future is ignored) are already settled here.
 */
export type VisitedSurface = 'routines' | 'tasks' | 'files' | 'apps' | 'home' | Home3CategorySurface;

/**
 * The Overview home's six category tiles, each remembering its OWN last visit.
 *
 * ⚠️ **Namespaced, so they are not the hub's surfaces.** `tasks` already exists above and badges the
 * Tasks hub tile; pressing the Tasks CATEGORY on the home is not the same event as opening the Tasks
 * screen, and sharing one stamp would have a tile press here silently clear a badge on a screen the
 * reader never went to. Different question, different key.
 */
export type Home3Category = 'tasks' | 'goals' | 'routines' | 'files' | 'apps' | 'outreach';
export type Home3CategorySurface = `home3:${Home3Category}`;

export const HOME3_CATEGORY_SURFACES: readonly Home3CategorySurface[] = [
  'home3:tasks',
  'home3:goals',
  'home3:routines',
  'home3:files',
  'home3:apps',
  'home3:outreach',
];

/** Every surface — the hub's, then `home`, then the Overview home's per-category stamps. */
export const VISITED_SURFACES: readonly VisitedSurface[] = [
  'routines',
  'tasks',
  'files',
  'apps',
  'home',
  ...HOME3_CATEGORY_SURFACES,
];

/**
 * The storage key for a surface.
 *
 * These are NEW keys, so they do NOT inherit the legacy `orion-web:` prefix that the older persisted
 * keys are stuck with — they are named for the PRODUCT (see this package's CLAUDE.md), the same way
 * `symphony-files-view` and `symphony-app-banner-dismissed` are.
 */
export function lastVisitedKey(surface: VisitedSurface): string {
  return `symphony-last-visited:${surface}`;
}

/** Parse a stored value into an epoch-ms stamp, or `null` when there isn't a usable one. Pure. */
export function parseLastVisited(raw: string | null | undefined): number | null {
  if (typeof raw !== 'string' || raw.trim() === '') return null;
  const ms = Number(raw);
  return Number.isFinite(ms) ? ms : null;
}

/** When this browser last opened the surface, or `null` — including when storage is unreadable. */
export function readLastVisited(surface: VisitedSurface, storage: Pick<Storage, 'getItem'> | null = safeStorage()): number | null {
  try {
    return parseLastVisited(storage?.getItem(lastVisitedKey(surface)));
  } catch {
    return null;
  }
}

/** Record that the reader is looking at the surface now. A failed write is not worth surfacing. */
export function markVisited(
  surface: VisitedSurface,
  nowMs: number = Date.now(),
  storage: Pick<Storage, 'setItem'> | null = safeStorage(),
): void {
  try {
    storage?.setItem(lastVisitedKey(surface), String(nowMs));
  } catch {
    // Private mode, a full quota, storage blocked by policy — the badge just stays generous.
  }
}

/** `localStorage`, or null where there isn't one — the access itself can throw, so it is guarded too. */
function safeStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}
